import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { bucketLabel } from '../utils/bucketLabels';
import './WordBankTriage.css';

// Word Bank Triage (2026-09-26): one row at a time, keyboard driven.
// Every bucket change goes through POST /api/wordbank/:id/bucket, which is the
// Chunk W3 move service (words update + word_moves row in one batch). Nothing
// here writes a bucket any other way, and nothing here creates a word.
// 2026-10-22: a click on a row's bucket badge opens a menu of the four buckets;
// a pick does exactly what that bucket's key does.
//
// Gate 1 (Chunk W-Gates): a word is approved only when Odin sets or confirms
// its bucket here. Pressing the key for the word's CURRENT bucket confirms it
// (POST /api/wordbank/:id/approve: stamp only, no auto-chain) and advances.
// Moving to another bucket stamps too. The 2026-09-12 audit buckets are not
// approvals, so every word starts unapproved.

type Bucket = 'Game' | 'Related' | 'Bench' | 'Rejected';
type Filter = Bucket | 'all';

const BUCKETS: Bucket[] = ['Game', 'Related', 'Bench', 'Rejected'];
// S (Social) is the key for the 'Related' bucket, now labelled Social Words; R still works.
const KEY_TO_BUCKET: Record<string, Bucket> = { g: 'Game', s: 'Related', r: 'Related', b: 'Bench', x: 'Rejected' };

const AXES = ['soundability', 'category_cleanliness', 'fun_fact_surface', 'difficulty', 'brand_fit', 'sensitivity'] as const;
type Axis = typeof AXES[number];
const AXIS_LABEL: Record<Axis, string> = {
  soundability: 'Snd', category_cleanliness: 'Cat', fun_fact_surface: 'Fact',
  difficulty: 'Diff', brand_fit: 'Brand', sensitivity: 'Safe',
};

interface TriageRow {
  id: string;
  word: string;
  bucket: Bucket;
  vet_definition: string | null;
  scores: Partial<Record<Axis, number>> | null;
  move_note: string | null;
  approved?: boolean;
}

interface ApprovalInfo {
  ready: boolean;
  error?: string;
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export default function WordBankTriage({ reloadKey = 0 }: { reloadKey?: number }) {
  const [rows, setRows] = useState<TriageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  // Rows moved this session stay in the list until the next load, so a row
  // does not vanish from under the cursor when it leaves the filtered bucket.
  const [touched, setTouched] = useState<Set<string>>(new Set());
  // On by default: approved words drop out of the list. Counts still cover every word.
  const [unapprovedOnly, setUnapprovedOnly] = useState(true);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [flash, setFlash] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  // Settings switch wordbank.auto_chain_enabled (default off). Null until read.
  const [autoChain, setAutoChain] = useState<boolean | null>(null);
  const [approvalInfo, setApprovalInfo] = useState<ApprovalInfo | null>(null);
  // Row id whose bucket menu is open.
  const [menuFor, setMenuFor] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/config/wordbank-auto-chain')
      .then((d) => setAutoChain((d as { enabled: boolean }).enabled))
      .catch(() => setAutoChain(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await api.get('/api/wordbank?review=all&scored=any')) as { rows: TriageRow[]; approval?: ApprovalInfo };
      setApprovalInfo(res.approval ?? null);
      const sorted = [...res.rows].sort((a, b) => a.word.localeCompare(b.word, undefined, { sensitivity: 'base' }));
      setRows(sorted);
      setTouched(new Set());
      setCursor(0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  // Click-outside and Escape close the bucket menu.
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      if (e instanceof MouseEvent && (e.target as HTMLElement | null)?.closest('.tri-bucket-cell')) return;
      setMenuFor(null);
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [menuFor]);

  const visible = useMemo(
    () => rows.filter((r) => touched.has(r.id)
      || ((filter === 'all' || r.bucket === filter) && (!unapprovedOnly || !r.approved))),
    [rows, filter, touched, unapprovedOnly],
  );

  const counts = useMemo(() => {
    const c: Record<Bucket, number> = { Game: 0, Related: 0, Bench: 0, Rejected: 0 };
    for (const r of rows) if (r.bucket in c) c[r.bucket] += 1;
    return c;
  }, [rows]);

  const approvedCount = useMemo(() => rows.filter((r) => r.approved).length, [rows]);

  const current = visible[Math.min(cursor, Math.max(visible.length - 1, 0))] as TriageRow | undefined;

  useEffect(() => {
    if (cursor > visible.length - 1 && visible.length > 0) setCursor(visible.length - 1);
  }, [cursor, visible.length]);

  useEffect(() => {
    if (current) rowRefs.current.get(current.id)?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  useEffect(() => { setCursor(0); }, [filter, unapprovedOnly]);

  const move = useCallback(async (row: TriageRow, to: Bucket) => {
    // The key for the current bucket CONFIRMS it (Gate 1 stamp) and advances.
    // It uses the approve route, not the bucket route, so the Game/Related
    // auto-chain never fires on a confirm. An already-approved word just advances.
    if (row.bucket === to) {
      if (row.approved) {
        setCursor((c) => Math.min(c + 1, visible.length - 1));
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await api.post(`/api/wordbank/${row.id}/approve`, {});
        setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, approved: true } : r)));
        setTouched((t) => new Set(t).add(row.id));
        setFlash(`${row.word}: ${bucketLabel(row.bucket)} approved`);
        setCursor((c) => Math.min(c + 1, visible.length - 1));
      } catch (err) {
        setError(`${row.word}: ${(err as Error).message}`);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = (await api.post(`/api/wordbank/${row.id}/bucket`, { bucket: to })) as { bucket: Bucket; stamped?: boolean };
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, bucket: res.bucket, approved: !!res.stamped } : r)));
      setTouched((t) => new Set(t).add(row.id));
      setFlash(`${row.word}: ${bucketLabel(row.bucket)} → ${bucketLabel(res.bucket)}`);
      setCursor((c) => Math.min(c + 1, visible.length - 1));
    } catch (err) {
      setError(`${row.word}: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [visible.length]);

  const saveNote = useCallback(async () => {
    if (!current) return;
    const text = noteText.trim();
    setBusy(true);
    try {
      // Same route, same bucket, with a note: the move service records it.
      await api.post(`/api/wordbank/${current.id}/bucket`, { bucket: current.bucket, note: text });
      setRows((rs) => rs.map((r) => (r.id === current.id ? { ...r, move_note: text || null } : r)));
      setNoteOpen(false);
    } catch (err) {
      setError(`${current.word}: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [current, noteText]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (!current || busy || menuFor) return;
      const k = e.key.toLowerCase();
      if (k in KEY_TO_BUCKET) {
        e.preventDefault();
        move(current, KEY_TO_BUCKET[k]);
      } else if (k === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, visible.length - 1));
      } else if (k === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (k === 'n') {
        e.preventDefault();
        setNoteText(current.move_note ?? '');
        setNoteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, busy, move, visible.length, menuFor]);

  return (
    <div className="tri">
      <div className="tri-bar">
        <div className="tri-counts">
          {BUCKETS.map((b) => (
            <span key={b} className={`wb-bucket wb-b-${b}`}>{bucketLabel(b)} <b>{counts[b]}</b></span>
          ))}
          <span className="tri-total">{rows.length} words</span>
        </div>
        <div className="tri-approval">
          {approvalInfo && !approvalInfo.ready
            ? <span className="tri-approval-missing">{approvalInfo.error}</span>
            : (
              <>
                <span className="tri-approved">Approved <b>{approvedCount}</b></span>
                <span className="tri-unapproved">Unapproved <b>{rows.length - approvedCount}</b></span>
              </>
            )}
        </div>
        <label className="tri-filter">
          <input type="checkbox" checked={unapprovedOnly} onChange={(e) => setUnapprovedOnly(e.target.checked)} />
          Unapproved only
        </label>
        <label className="tri-filter">
          Current bucket
          <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">All</option>
            {BUCKETS.map((b) => <option key={b} value={b}>{bucketLabel(b)}</option>)}
          </select>
        </label>
        <button className="wb-refresh" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Reload'}</button>
      </div>

      <div className="tri-keys">
        <kbd>G</kbd> Game <kbd>S</kbd> Social only <kbd>B</kbd> Bench <kbd>X</kbd> Rejected
        <span className="tri-sep" /> <kbd>J</kbd>/<kbd>K</kbd> or arrows move <kbd>N</kbd> note
        <span className="tri-hint">
          A key, or a pick from a row's bucket menu, saves, approves and advances. The key for the word's current bucket confirms it.{' '}
          {autoChain === true
            ? 'Auto-chain is ON in Settings: Game and Social moves start audit and Stage 1.'
            : autoChain === false
              ? 'Auto-chain is off in Settings: a move is just a move, no content is generated.'
              : ''}
        </span>
      </div>

      {error && <div className="wb-error">{error}</div>}
      {flash && <div className="tri-flash">{flash}</div>}

      {current && noteOpen && (
        <div className="tri-note">
          <span>Note for <b>{current.word}</b> ({bucketLabel(current.bucket)})</span>
          <input
            type="text"
            autoFocus
            maxLength={500}
            placeholder="Why? (optional)"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveNote();
              if (e.key === 'Escape') setNoteOpen(false);
            }}
          />
          <button className="wb-move" onClick={saveNote} disabled={busy}>Save</button>
          <button className="wb-move" onClick={() => setNoteOpen(false)}>Cancel</button>
        </div>
      )}

      <div className="tri-layout">
        <div className="tri-main">
          {!loading && visible.length === 0 && <div className="web-empty">{unapprovedOnly ? 'No unapproved words here.' : 'No words in this bucket.'}</div>}
          {visible.length > 0 && (
            <table className="tri-table">
              <thead>
                <tr>
                  <th>Word</th>
                  <th>Definition</th>
                  <th>Bucket</th>
                  <th title="Approved by Odin in Triage (Gate 1)">Approved</th>
                  {AXES.map((a) => <th key={a} className="tri-score">{AXIS_LABEL[a]}</th>)}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr
                    key={r.id}
                    ref={(el) => { if (el) rowRefs.current.set(r.id, el); else rowRefs.current.delete(r.id); }}
                    className={current && current.id === r.id ? 'tri-row tri-active' : 'tri-row'}
                    onClick={() => setCursor(i)}
                  >
                    <td className="tri-word">
                      {r.word}
                      {r.move_note && <div className="tri-rownote" title={r.move_note}>“{r.move_note}”</div>}
                    </td>
                    <td className="tri-def" title={r.vet_definition || undefined}>
                      {r.vet_definition || <span className="wb-dash">no definition</span>}
                    </td>
                    <td className="tri-bucket-cell">
                      <button
                        type="button"
                        className={`wb-bucket wb-b-${r.bucket} tri-bucket-btn`}
                        aria-haspopup="menu"
                        aria-expanded={menuFor === r.id}
                        title="Move or confirm this word's bucket"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); setCursor(i); setMenuFor(menuFor === r.id ? null : r.id); }}
                      >
                        {bucketLabel(r.bucket)} ▾
                      </button>
                      {menuFor === r.id && (
                        <div className="tri-bucket-menu" role="menu">
                          {BUCKETS.map((b) => (
                            <button
                              key={b}
                              type="button"
                              role="menuitem"
                              className={b === r.bucket ? 'tri-bucket-item current' : 'tri-bucket-item'}
                              onClick={(e) => { e.stopPropagation(); setMenuFor(null); void move(r, b); }}
                            >
                              {bucketLabel(b)}{b === r.bucket ? (r.approved ? ' (current)' : ' (confirm)') : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="tri-stamp">{r.approved ? <span className="tri-stamp-ok" title="Approved">✓</span> : null}</td>
                    {AXES.map((a) => {
                      const v = r.scores?.[a];
                      return (
                        <td key={a} className="tri-score">
                          {typeof v === 'number' ? <span className={`wb-score wb-s${v}`}>{v}</span> : <span className="wb-dash">·</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
      {current && <div className="tri-foot">{current.word} · {Math.min(cursor, visible.length - 1) + 1} of {visible.length}</div>}
    </div>
  );
}
